import { useEffect, useRef, useState } from "react";
import {
  ShieldCheck,
  Hash,
  ChevronRight,
  Loader2,
  Lock,
  Fingerprint,
  Sparkles,
  Clock4,
  CheckCircle2,
  Cpu,
} from "lucide-react";

/**
 * Hero card — "Blindaje Antiplagio Criptográfico".
 * Client-only visual mock. Backend integration arrives in next prompt.
 */
export default function BlindajeHeroCard() {
  const [state, setState] = useState("idle"); // idle | sealing | sealed
  const [hash, setHash] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [sealedAt, setSealedAt] = useState(null);
  const timersRef = useRef([]);

  const STEPS = [
    "Calculando SHA-256 del manuscrito…",
    "Anclando huella en el historial inmutable…",
    "Generando prueba de autoría matemática…",
    "Sellando timestamp criptográfico…",
  ];

  const startSealing = () => {
    if (state !== "idle") return;
    setState("sealing");
    setStepIndex(0);
    // animated hash chars
    const hashInterval = window.setInterval(() => {
      setHash(_randomHash(64));
    }, 70);
    timersRef.current.push(hashInterval);

    // step progression
    STEPS.forEach((_, i) => {
      const t = window.setTimeout(() => setStepIndex(i), 700 * (i + 1));
      timersRef.current.push(t);
    });

    // finalize
    const final = window.setTimeout(() => {
      window.clearInterval(hashInterval);
      setHash(_finalHash());
      setSealedAt(new Date());
      setState("sealed");
    }, 700 * (STEPS.length + 1));
    timersRef.current.push(final);
  };

  const reset = () => {
    timersRef.current.forEach((t) => {
      window.clearTimeout(t);
      window.clearInterval(t);
    });
    timersRef.current = [];
    setHash("");
    setSealedAt(null);
    setStepIndex(0);
    setState("idle");
  };

  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => {
        window.clearTimeout(t);
        window.clearInterval(t);
      });
    };
  }, []);

  return (
    <section
      data-testid="hero-blindaje-card"
      className="relative mt-16 lg:mt-20"
    >
      <div className="relative overflow-hidden rounded-[28px] bg-[#1d2c22] text-paper shadow-[0_30px_80px_-20px_rgba(20,32,24,0.55)] ring-1 ring-white/5">
        {/* Background — grid + ornament */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 opacity-[0.07] bg-[linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] bg-[size:42px_42px]" />
          <div className="absolute -top-28 -right-24 h-[420px] w-[420px] rounded-full bg-[radial-gradient(closest-side,rgba(193,148,86,0.35),transparent_70%)] blur-2xl" />
          <div className="absolute -bottom-32 -left-20 h-[360px] w-[360px] rounded-full bg-[radial-gradient(closest-side,rgba(157,178,153,0.18),transparent_70%)] blur-2xl" />
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 px-6 sm:px-10 lg:px-14 py-10 lg:py-14">
          {/* Left: copy + CTA */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center h-8 w-8 rounded-full bg-copper/15 ring-1 ring-copper/40">
                <ShieldCheck size={15} className="text-copper" />
              </span>
              <p className="text-[10px] tracking-[0.28em] uppercase text-copper font-medium">
                Blindaje Antiplagio Criptográfico
              </p>
            </div>

            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-[2.7rem] leading-[1.08] text-paper text-balance">
              Tu tesis,{" "}
              <span className="italic text-[#E0C58A]">sellada</span> en un
              registro que nadie puede falsificar.
            </h2>

            <p className="mt-4 text-[14.5px] leading-relaxed text-paper/75 max-w-md">
              Antes de compartirla con cualquier asesor, congelamos una huella
              matemática SHA-256 de tu manuscrito en un historial inmutable.
              Si alguien intenta robar tu investigación, tendrás la prueba
              histórica de que <span className="text-paper">tú</span> fuiste
              quien la escribió primero.
            </p>

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12.5px]">
              <Bullet icon={Fingerprint} text="Huella SHA-256 verificable" />
              <Bullet icon={Clock4} text="Timestamp anclado por bloque" />
              <Bullet icon={Lock} text="Prueba inmutable de autoría" />
              <Bullet icon={Cpu} text="0 acceso humano a tu contenido" />
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              {state !== "sealed" ? (
                <button
                  data-testid="blindaje-seal-btn"
                  onClick={startSealing}
                  disabled={state === "sealing"}
                  className="group relative inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[14px] font-medium text-[#1d2c22] bg-[#E0C58A] hover:bg-[#EBD8A8] disabled:opacity-80 transition-all shadow-[0_18px_36px_-12px_rgba(224,197,138,0.55)]"
                >
                  {/* halo pulse */}
                  <span
                    aria-hidden="true"
                    className={`absolute inset-0 rounded-full ${
                      state === "sealing"
                        ? "animate-ping bg-[#E0C58A]/60"
                        : "opacity-0"
                    }`}
                  />
                  {state === "sealing" ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ShieldCheck size={14} />
                  )}
                  <span className="relative">
                    {state === "sealing"
                      ? "Sellando…"
                      : "Sellar en el Historial Inmutable"}
                  </span>
                  {state === "idle" && (
                    <ChevronRight
                      size={14}
                      className="relative transition-transform group-hover:translate-x-0.5"
                    />
                  )}
                </button>
              ) : (
                <button
                  data-testid="blindaje-reset-btn"
                  onClick={reset}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] text-paper/85 bg-white/5 hover:bg-white/10 border border-white/10"
                >
                  <Sparkles size={13} /> Probar otra vez
                </button>
              )}
              <p className="text-[11.5px] text-paper/55 max-w-[220px] leading-snug">
                Funciona aunque borres tu cuenta. La prueba sigue ahí.
              </p>
            </div>
          </div>

          {/* Right: animated console */}
          <div className="relative">
            <div
              data-testid="blindaje-console"
              data-state={state}
              className="rounded-2xl bg-[#15201a] ring-1 ring-white/10 overflow-hidden"
            >
              {/* Console header */}
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 bg-black/30">
                <span className="h-2.5 w-2.5 rounded-full bg-[#E55E5E]/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#E0C58A]/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#9DB299]/80" />
                <p className="ml-3 text-[10.5px] tracking-[0.22em] uppercase text-paper/55 font-medium">
                  Notario digital · unaTESIS
                </p>
                <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] text-paper/55">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#9DB299] animate-pulse" />
                  Online
                </span>
              </div>

              {/* Console body */}
              <div className="px-5 py-5 min-h-[280px] font-mono text-[12px] text-paper/85 leading-relaxed">
                {state === "idle" && (
                  <IdleView />
                )}

                {state === "sealing" && (
                  <SealingView steps={STEPS} stepIndex={stepIndex} hash={hash} />
                )}

                {state === "sealed" && (
                  <SealedView hash={hash} sealedAt={sealedAt} />
                )}
              </div>
            </div>

            {/* Floating success message (also shown below console for clarity) */}
            {state === "sealed" && (
              <div
                data-testid="blindaje-success-message"
                className="mt-4 rounded-2xl bg-[#E0C58A]/12 ring-1 ring-[#E0C58A]/35 px-4 py-3 flex items-start gap-3"
              >
                <span className="grid place-items-center h-7 w-7 shrink-0 rounded-full bg-[#E0C58A] text-[#1d2c22]">
                  <CheckCircle2 size={14} />
                </span>
                <p className="text-[12.5px] leading-relaxed text-paper">
                  Tu trabajo ha sido sellado criptográficamente. Tienes pruebas
                  matemáticas e históricas de que tú eres el autor original de
                  esta investigación ante cualquier intento de robo o copia.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- subviews ---------- */

function Bullet({ icon: Icon, text }) {
  return (
    <li className="inline-flex items-center gap-2 rounded-lg bg-white/5 ring-1 ring-white/10 px-3 py-2 text-paper/85">
      <Icon size={13} className="text-[#E0C58A]" />
      <span>{text}</span>
    </li>
  );
}

function IdleView() {
  return (
    <div className="flex flex-col gap-3">
      <ConsoleLine label="status" value="esperando_solicitud" tone="muted" />
      <ConsoleLine label="archivo" value="aprendizaje_invertido.pdf" />
      <ConsoleLine label="autor" value="andrea.lopez@unam" />
      <ConsoleLine label="algoritmo" value="SHA-256 + Merkle tree" />
      <ConsoleLine label="ancla" value="ledger.unatesis.mx" />
      <div className="mt-4 grid grid-cols-8 gap-1">
        {Array.from({ length: 24 }).map((_, i) => (
          <span
            key={i}
            className="h-1 rounded-full bg-white/10"
            style={{ opacity: 0.3 + (i % 8) / 16 }}
          />
        ))}
      </div>
      <p className="mt-2 text-[11px] text-paper/45">
        Presiona <span className="text-[#E0C58A]">Sellar en el Historial Inmutable</span> para iniciar.
      </p>
    </div>
  );
}

function SealingView({ steps, stepIndex, hash }) {
  return (
    <div className="flex flex-col gap-3" data-testid="blindaje-sealing">
      {/* Hash spinner */}
      <div className="rounded-xl bg-black/35 px-3.5 py-3 ring-1 ring-[#E0C58A]/20">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#E0C58A]/85">
          <Hash size={11} /> Calculando huella
        </div>
        <p
          data-testid="blindaje-hash"
          className="mt-1.5 text-[12px] text-[#E0C58A] break-all leading-tight tracking-tight"
        >
          {hash || "···"}
        </p>
      </div>

      {/* Steps */}
      <ul className="flex flex-col gap-1.5 mt-1">
        {steps.map((s, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          return (
            <li
              key={i}
              className={`flex items-center gap-2 text-[12px] ${
                done ? "text-paper/65" : active ? "text-paper" : "text-paper/40"
              }`}
            >
              <span
                className={`grid place-items-center h-4 w-4 rounded-full ${
                  done
                    ? "bg-[#9DB299] text-[#1d2c22]"
                    : active
                    ? "bg-[#E0C58A] text-[#1d2c22] animate-pulse"
                    : "bg-white/10"
                }`}
              >
                {done ? (
                  <CheckCircle2 size={9} />
                ) : active ? (
                  <Loader2 size={9} className="animate-spin" />
                ) : (
                  <span className="h-1 w-1 rounded-full bg-white/40" />
                )}
              </span>
              {s}
            </li>
          );
        })}
      </ul>

      {/* Chain animation */}
      <div className="mt-2 flex items-center gap-1.5">
        {Array.from({ length: 7 }).map((_, i) => (
          <span
            key={i}
            className="h-1.5 w-7 rounded-full bg-[#E0C58A]/70 animate-pulse"
            style={{ animationDelay: `${i * 90}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function SealedView({ hash, sealedAt }) {
  const ts = sealedAt
    ? sealedAt.toLocaleString("es-MX", {
        dateStyle: "medium",
        timeStyle: "medium",
      })
    : "—";
  const blockId = "0x" + (hash || "").slice(0, 8).toUpperCase();
  return (
    <div className="flex flex-col gap-3" data-testid="blindaje-sealed">
      <div className="rounded-xl bg-black/40 px-3.5 py-3 ring-1 ring-[#9DB299]/35">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#9DB299]">
          <Hash size={11} /> Huella sellada · SHA-256
        </div>
        <p
          data-testid="blindaje-final-hash"
          className="mt-1.5 text-[11.5px] text-[#9DB299] break-all leading-tight tracking-tight"
        >
          {hash}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Tile label="Bloque" value={blockId} />
        <Tile label="Algoritmo" value="SHA-256" />
        <Tile label="Sellado el" value={ts} wide />
      </div>

      <p className="text-[11px] text-paper/60 leading-snug">
        Esta huella queda anclada en el historial.{" "}
        <span className="text-[#9DB299]">Inalterable.</span> Verificable por
        cualquier perito.
      </p>
    </div>
  );
}

function ConsoleLine({ label, value, tone }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10px] tracking-[0.22em] uppercase text-paper/40 w-20">
        {label}
      </span>
      <span
        className={`text-[11.5px] ${
          tone === "muted" ? "text-paper/55" : "text-paper/90"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function Tile({ label, value, wide }) {
  return (
    <div
      className={`rounded-lg bg-white/5 ring-1 ring-white/10 px-3 py-2 ${
        wide ? "col-span-2" : ""
      }`}
    >
      <p className="text-[9.5px] tracking-[0.22em] uppercase text-paper/45 font-medium">
        {label}
      </p>
      <p className="mt-0.5 text-[11.5px] text-paper/90 font-mono break-all">
        {value}
      </p>
    </div>
  );
}

/* ---------- helpers ---------- */

const HEX = "0123456789abcdef";
function _randomHash(n) {
  let s = "";
  for (let i = 0; i < n; i++) s += HEX[Math.floor(Math.random() * HEX.length)];
  return s;
}
function _finalHash() {
  // Pretty, fixed-looking mock hash for the success state
  return _randomHash(64);
}
